/**
 * useSystemKillSwitches - Polls system_settings for kill switch flags
 * Used for emergency fail-open mechanisms (e.g., bypass content gating)
 */
import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { db } from '@/lib/supabaseQuery';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';
import { useLocalizations } from '@/hooks/useLocalizations';

let killSwitchSubscribers = 0;
let killSwitchChannel: ReturnType<typeof supabase.channel> | null = null;

interface KillSwitchState {
  bypassContentGating: boolean;
  isLoading: boolean;
  toggleBypassContentGating: () => Promise<void>;
}

export function useSystemKillSwitches(): KillSwitchState {
  const queryClient = useQueryClient();
  const { lCommon } = useLocalizations();

  const { data, isLoading } = useQuery({
    queryKey: ['system-kill-switches'],
    queryFn: async () => {
      const rows = await db.fetchAll<{ setting_key: string; setting_value: unknown }>(
        'system_settings',
        'setting_key, setting_value',
        {
          filters: [
            { column: 'setting_key', operator: 'in', value: ['bypass_content_gating'] }
          ]
        }
      );
      const map: Record<string, unknown> = {};
      for (const row of rows) {
        map[row.setting_key] = row.setting_value;
      }
      return {
        bypassContentGating: map.bypass_content_gating === true || map.bypass_content_gating === 'true',
      };
    },
    refetchInterval: 5 * 60_000,
    staleTime: 10_000,
    placeholderData: (previousData) => previousData,
  });

  useEffect(() => {
    killSwitchSubscribers += 1;
    if (!killSwitchChannel) {
      killSwitchChannel = supabase
        .channel(`system-kill-switches-${Math.random().toString(36).slice(2)}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'system_settings',
            filter: 'setting_key=eq.bypass_content_gating',
          },
          () => {
            void queryClient.invalidateQueries({ queryKey: ['system-kill-switches'] });
          },
        )
        .subscribe();
    }

    return () => {
      killSwitchSubscribers -= 1;
      if (killSwitchSubscribers === 0 && killSwitchChannel) {
        void supabase.removeChannel(killSwitchChannel);
        killSwitchChannel = null;
      }
    };
  }, [queryClient]);

  const toggleSetting = async (key: string, currentValue: boolean, successOn: string, successOff: string) => {
    const newValue = !currentValue;
    try {
      await db.upsert(
        'system_settings',
        { setting_key: key, setting_value: String(newValue) },
        { onConflict: 'setting_key' }
      );
      await queryClient.invalidateQueries({ queryKey: ['system-kill-switches'] });
      toast.success(newValue ? successOn : successOff);
    } catch (error) {
      logger.error(`Failed to toggle ${key}`, error);
      toast.error(lCommon('toast.system_settings.toggle_failed', { defaultValue: 'Failed to toggle setting' }));
    }
  };

  const toggleBypassContentGating = () =>
    toggleSetting(
      'bypass_content_gating',
      data?.bypassContentGating ?? false,
      'Content gating bypassed',
      'Content gating restored'
    );

  return {
    bypassContentGating: data?.bypassContentGating ?? false,
    isLoading,
    toggleBypassContentGating,
  };
}
